import React, { useState } from "react";
import styled from "styled-components";
import Container from "../Common/Container";
import { contactService } from "../../api/contactService";

const Wrapper = styled.section`
  max-width: 500px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem 1.5rem;
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.07);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: 2.2rem;
  color: #e97d7dff;
  margin-bottom: 1.2rem;
  text-align: center;
`;

const Text = styled.p`
  font-size: 1.1rem;
  color: #444;
  margin-bottom: 2rem;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Input = styled.input`
  padding: 0.9rem 1rem;
  border: 1.5px solid #e97d7dff;
  border-radius: 0.7rem;
  font-size: 1rem;
  outline: none;
  transition: border 0.2s;
  &:focus {
    border-color: #000;
  }
`;

const Textarea = styled.textarea`
  padding: 0.9rem 1rem;
  border: 1.5px solid #e97d7dff;
  border-radius: 0.7rem;
  font-size: 1rem;
  min-height: 110px;
  outline: none;
  transition: border 0.2s;
  resize: vertical;
  &:focus {
    border-color: #000;
  }
`;

const Button = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }
`;

const SuccessMsg = styled.div`
  color: #1ca97a;
  font-weight: 600;
  margin-top: 1.2rem;
  text-align: center;
`;

const ErrorMsg = styled.div`
  color: #e74c3c;
  font-weight: 600;
  margin-top: 1.2rem;
  text-align: center;
`;

const LoadingMsg = styled.div`
  color: #e97d7d;
  font-weight: 600;
  margin-top: 1.2rem;
  text-align: center;
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      await contactService.createMessage(formData);
      setStatus("success");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to send message"
      );
    }
  };
  return (
    <section style={{ padding: "3rem 0" }}>
      <Container>
        <Wrapper>
          <Title>Contact Us</Title>
          <Text>
            Have a question, custom request, or just want to say hello? Fill out
            the form below and we'll get back to you soon!
          </Text>
          <Form onSubmit={handleSubmit}>
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <Input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <Textarea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleInputChange}
              required
            />
            <Button type="submit" disabled={status === "loading"}>
              {status === "loading" ? "Sending..." : "Send Message"}
            </Button>
          </Form>
          {status === "success" && (
            <SuccessMsg>Thank you! We'll be in touch soon.</SuccessMsg>
          )}
          {status === "error" && <ErrorMsg>{errorMessage}</ErrorMsg>}
          {status === "loading" && (
            <LoadingMsg>Sending your message...</LoadingMsg>
          )}
        </Wrapper>
      </Container>
    </section>
  );
};

export default Contact;
